import React, { useRef, useCallback, useEffect } from 'react';
import { User, Bell } from 'lucide-react';
import { getCookie } from '../utils/utils';
import NotificationsPanel, { AppNotification } from './NotificationsPanel';

interface UserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
}

interface TopNavBarProps {
  userData?: UserData | null;
  onNavigate?: (itemId: string) => void;
  onGoHome?: () => void;
  onSecretProjectSeeded?: (payload: {
    projectId: number;
    name?: string;
    recipeBlueprintId?: number;
    recipeCombinationId?: number;
  }) => void;
  notifications?: AppNotification[];
  unreadCount?: number;
  notificationsOpen?: boolean;
  onToggleNotifications?: () => void;
  onCloseNotifications?: () => void;
  onOpenNotification?: (notification: AppNotification) => void;
  onMarkAllNotificationsRead?: () => void;
}

const SECRET_CLICK_TARGET = 5;
const PROFILE_NAV_DELAY_MS = 400;

const TopNavBar: React.FC<TopNavBarProps> = ({
  userData,
  onNavigate,
  onGoHome,
  onSecretProjectSeeded,
  notifications = [],
  unreadCount = 0,
  notificationsOpen = false,
  onToggleNotifications,
  onCloseNotifications,
  onOpenNotification,
  onMarkAllNotificationsRead,
}) => {
  const initials = userData
    ? `${userData.first_name?.[0] || ''}${userData.last_name?.[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const profileClickCountRef = useRef(0);
  const profileClickTimerRef = useRef<number | null>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const clearProfileClickTimer = useCallback(() => {
    if (profileClickTimerRef.current !== null) {
      window.clearTimeout(profileClickTimerRef.current);
      profileClickTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!notificationsOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        onCloseNotifications?.();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notificationsOpen, onCloseNotifications]);

  const handleProfileClick = async () => {
    clearProfileClickTimer();
    profileClickCountRef.current += 1;

    if (profileClickCountRef.current >= SECRET_CLICK_TARGET) {
      profileClickCountRef.current = 0;
      try {
        const response = await fetch('/api/secret/demo-project/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken') || '',
          },
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && typeof data.project_id === 'number') {
          onSecretProjectSeeded?.({
            projectId: data.project_id,
            name: typeof data.name === 'string' ? data.name : undefined,
            recipeBlueprintId:
              typeof data.recipe_blueprint_id === 'number' ? data.recipe_blueprint_id : undefined,
            recipeCombinationId:
              typeof data.recipe_combination_id === 'number' ? data.recipe_combination_id : undefined,
          });
        }
      } catch {
        // Silent easter egg — no error UI.
      }
      return;
    }

    profileClickTimerRef.current = window.setTimeout(() => {
      profileClickCountRef.current = 0;
      onNavigate?.('profile');
    }, PROFILE_NAV_DELAY_MS);
  };

  return (
    <header className="flex-1 h-12 px-6 flex items-center justify-end bg-[#EAE9F2] border-b border-[#E0DEE8]">
      <div className="flex items-center gap-3">
        <div className="relative" ref={notificationsRef}>
          <button
            type="button"
            onClick={onToggleNotifications}
            className="relative flex items-center justify-center w-[34px] h-[33px] rounded-full bg-[#D4D0E8] hover:bg-[#C8C4E0] focus:outline-none transition-colors"
            title="Notifications"
            aria-label="Notifications"
            aria-expanded={notificationsOpen}
          >
            <Bell className="w-[18px] h-[18px] text-white" strokeWidth={2} />
            {unreadCount > 0 && (
              <span
                className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[#EAE9F2]"
                aria-hidden
              />
            )}
          </button>
          {notificationsOpen && (
            <NotificationsPanel
              notifications={notifications}
              onClose={() => onCloseNotifications?.()}
              onOpenNotification={(n) => onOpenNotification?.(n)}
              onMarkAllRead={() => onMarkAllNotificationsRead?.()}
            />
          )}
        </div>

        <button
          type="button"
          onClick={handleProfileClick}
          className="flex-shrink-0 focus:outline-none"
          title="Profile"
        >
          {userData?.profile_picture ? (
            <img
              src={userData.profile_picture}
              alt="Profile"
              className="w-[33px] h-[33px] rounded-full object-cover ring-2 ring-[#655FA8]/20"
            />
          ) : (
            <div className="w-[33px] h-[33px] rounded-full bg-[#655FA8] flex items-center justify-center text-white text-sm font-medium">
              {initials !== 'U' ? initials : <User className="w-4 h-4" />}
            </div>
          )}
        </button>
      </div>
    </header>
  );
};

export default TopNavBar;
